import { createFileRoute, type LinkProps, useNavigate } from "@tanstack/react-router";
import {
  BuildingIcon,
  FileSpreadsheetIcon,
  FlagIcon,
  LandmarkIcon,
  ListTodoIcon,
  type LucideIcon,
  SearchIcon,
  Trash2Icon,
} from "lucide-react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { useCurrentUser } from "@/hooks/use-current-user";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authed/_dashboard/admin-tools/")({
  component: AdminToolsPage,
});

interface AdminTool {
  id: string;
  name: string;
  description: string;
  icon: LucideIcon;
  iconBgColor: string;
  iconColor: string;
  /** Plain string: /agencies is migrated by a separate route file (parity with the Vue router). */
  to: string;
}

const tools: AdminTool[] = [
  {
    id: "mass-abandon",
    name: "Abandon en masse",
    description:
      "Abandonner plusieurs dossiers en une seule opération, typiquement suite à une circonstance extraordinaire (météo, grève).",
    icon: Trash2Icon,
    iconBgColor: "bg-red-100",
    iconColor: "text-red-600",
    to: "/admin-tools/mass-abandon",
  },
  {
    id: "agencies",
    name: "Assignation des juristes",
    description: "Associer les juristes responsables à chaque office.",
    icon: BuildingIcon,
    iconBgColor: "bg-blue-100",
    iconColor: "text-blue-600",
    to: "/agencies",
  },
  {
    id: "timeline-search",
    name: "Recherche par timeline",
    description: "Rechercher dans les notes internes de la timeline des dossiers.",
    icon: SearchIcon,
    iconBgColor: "bg-purple-100",
    iconColor: "text-purple-600",
    to: "/admin-tools/timeline-search",
  },
  {
    id: "finance",
    name: "Suivi financier",
    description:
      "Ordres de paiement SEPA, quittances des passagers et paiements agences, chacun dans son onglet.",
    icon: LandmarkIcon,
    iconBgColor: "bg-green-100",
    iconColor: "text-green-600",
    to: "/admin-tools/finance",
  },
  {
    id: "all-tasks",
    name: "Toutes les tâches",
    description: "Vue d'ensemble de toutes les tâches de l'équipe, avec filtrage par juriste.",
    icon: ListTodoIcon,
    iconBgColor: "bg-orange-100",
    iconColor: "text-orange-600",
    to: "/admin-tools/all-tasks",
  },
  {
    id: "feature-flags",
    name: "Feature flags",
    description: "Gérer les feature flags et activer/désactiver chaque fonctionnalité par entreprise.",
    icon: FlagIcon,
    iconBgColor: "bg-green-100",
    iconColor: "text-green-600",
    to: "/admin-tools/feature-flags",
  },
  {
    id: "reporting",
    name: "Reporting",
    description: "Générer à la demande un rapport planifié (financier, dossiers, IBAN, factures…) dans Box.",
    icon: FileSpreadsheetIcon,
    iconBgColor: "bg-sky-100",
    iconColor: "text-sky-600",
    to: "/admin-tools/reporting",
  },
];

/** Port of AdminTools.vue — static tile grid; role gate mirrors the Vue nav (Upclaim). */
function AdminToolsPage() {
  const { can } = useCurrentUser();
  const navigate = useNavigate();

  if (!can(UPCLAIM_ROLES)) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <h1 className="text-4xl font-bold">404</h1>
        <p className="text-muted-foreground">Cette page n'existe pas.</p>
      </div>
    );
  }

  return (
    <div className="w-full grow bg-gray-50">
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Outils Admin</h1>
          <p className="mt-1 text-sm text-gray-500">
            Outils d'administration pour les opérations en masse et la gestion avancée.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {tools.map((tool) => (
            <button
              key={tool.id}
              type="button"
              onClick={() => navigate({ to: tool.to as NonNullable<LinkProps["to"]> })}
              className="group relative flex items-start gap-4 rounded-lg border border-gray-200 bg-white p-4 text-left shadow-sm transition-all hover:border-gray-300 hover:shadow-md"
            >
              <div
                className={cn(
                  tool.iconBgColor,
                  "flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg",
                )}
              >
                <tool.icon className={cn(tool.iconColor, "h-6 w-6")} />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900 group-hover:text-gray-700">{tool.name}</h3>
                <p className="mt-1 text-sm text-gray-500">{tool.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
